import { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Users as UsersIcon, 
    UserPlus, 
    Trash2, 
    Loader2,
    Shield,
    User,
    Key,
    RefreshCw,
    Copy,
    Check,
    AlertTriangle
} from 'lucide-react';

const Users = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'user',
    });
    const [creating, setCreating] = useState(false);
    
    // Password reset states
    const [selectedUser, setSelectedUser] = useState(null);
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [resettingPassword, setResettingPassword] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            setUsers(response.data);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!formData.username || !formData.password || !formData.full_name) {
            toast.error('Please fill in all fields');
            return;
        }

        setCreating(true);
        try {
            await usersAPI.create(formData);
            toast.success('User created successfully!');
            setShowDialog(false);
            setFormData({ username: '', password: '', full_name: '', email: '', role: 'user' });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create user');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (userId) => {
        if (userId === currentUser?.id) {
            toast.error('You cannot delete your own account');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            await usersAPI.delete(userId);
            toast.success('User deleted');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    const generateRandomPassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    const handleOpenResetDialog = (user) => {
        if (user.id === currentUser?.id) {
            toast.error('Cannot reset your own password from here. Use the profile settings.');
            return;
        }
        setSelectedUser(user);
        setNewPassword(generateRandomPassword());
        setResetSuccess(false);
        setGeneratedPassword('');
        setCopied(false);
        setShowResetDialog(true);
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 4) {
            toast.error('Password must be at least 4 characters');
            return;
        }

        setResettingPassword(true);
        try {
            const response = await usersAPI.resetPassword(selectedUser.id, newPassword);
            setGeneratedPassword(response.data.new_password);
            setResetSuccess(true);
            toast.success('Password reset successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setResettingPassword(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(generatedPassword);
        setCopied(true);
        toast.success('Password copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    return (
        <div data-testid="users-page" className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">User Management</h1>
                    <p className="text-gray-500 mt-1">Manage system users and access</p>
                </div>
                <Button
                    data-testid="add-user-btn"
                    onClick={() => setShowDialog(true)}
                    className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-6"
                >
                    <UserPlus size={18} className="mr-2" />
                    Add User
                </Button>
            </div>

            {/* Security Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Shield className="text-blue-600 mt-0.5" size={20} />
                <div>
                    <h3 className="font-medium text-blue-900">Secure Password Management</h3>
                    <p className="text-sm text-blue-700 mt-1">
                        Passwords are securely hashed. Admins can reset user passwords but cannot view existing ones.
                    </p>
                </div>
            </div>

            {/* Users Table */}
            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <UsersIcon className="text-[#C08478]" size={20} />
                        All Users ({users.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F5F5F5]">
                                    <TableHead className="font-medium">User</TableHead>
                                    <TableHead className="font-medium">Username</TableHead>
                                    <TableHead className="font-medium">Email</TableHead>
                                    <TableHead className="font-medium">Role</TableHead>
                                    <TableHead className="font-medium">Created</TableHead>
                                    <TableHead className="font-medium">Password</TableHead>
                                    <TableHead className="text-right font-medium">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id} className="hover:bg-[#FAFAFA]">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#C08478]/20 flex items-center justify-center">
                                                    <span className="text-[#C08478] font-semibold">
                                                        {user.full_name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#1A1A1A]">{user.full_name}</p>
                                                    {user.id === currentUser?.id && (
                                                        <span className="text-xs text-gray-400">(You)</span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            {user.username}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {user.email ? (
                                                <span className="text-gray-600">{user.email}</span>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">not set</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`${
                                                user.role === 'admin' 
                                                    ? 'bg-[#C08478] text-white' 
                                                    : 'bg-[#F5F5F5] text-[#1A1A1A]'
                                            } font-medium`}>
                                                {user.role === 'admin' ? (
                                                    <><Shield size={12} className="mr-1" /> Admin</>
                                                ) : (
                                                    <><User size={12} className="mr-1" /> User</>
                                                )}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-gray-600">
                                            {formatDate(user.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {user.id !== currentUser?.id ? (
                                                <Button
                                                    data-testid={`reset-password-${user.id}`}
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleOpenResetDialog(user)}
                                                    className="rounded-full text-xs"
                                                >
                                                    <RefreshCw size={14} className="mr-1" />
                                                    Reset
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-gray-400">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {user.id !== currentUser?.id && (
                                                <Button
                                                    data-testid={`delete-user-${user.id}`}
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(user.id)}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <Trash2 size={18} />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New User</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="fullName">Full Name</Label>
                            <Input
                                id="fullName"
                                data-testid="new-user-fullname"
                                value={formData.full_name}
                                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                placeholder="John Doe"
                                className="bg-[#F5F5F5] border-[#E5E5E5]"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="username">Username</Label>
                            <Input
                                id="username"
                                data-testid="new-user-username"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                placeholder="johndoe"
                                className="bg-[#F5F5F5] border-[#E5E5E5]"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email <span className="text-xs text-gray-400">(receives alerts)</span></Label>
                            <Input
                                id="email"
                                data-testid="new-user-email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john@company.com"
                                className="bg-[#F5F5F5] border-[#E5E5E5]"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                data-testid="new-user-password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="••••••••"
                                className="bg-[#F5F5F5] border-[#E5E5E5]"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select 
                                value={formData.role} 
                                onValueChange={(val) => setFormData({ ...formData, role: val })}
                            >
                                <SelectTrigger data-testid="new-user-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">
                                        <div className="flex items-center gap-2">
                                            <User size={14} />
                                            Normal User
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="admin">
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} />
                                            Administrator
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDialog(false)}
                            className="rounded-full"
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="create-user-submit"
                            onClick={handleCreate}
                            disabled={creating}
                            className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full"
                        >
                            {creating ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Creating...
                                </>
                            ) : (
                                'Create User'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key size={18} className="text-[#C08478]" />
                            Reset Password
                        </DialogTitle>
                        <DialogDescription>
                            Generate a new password for this user
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedUser && (
                        <div className="space-y-6 py-4">
                            <div className="bg-[#F5F5F5] rounded-xl p-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500">User</span>
                                    <span className="font-medium">{selectedUser.full_name}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Username</span>
                                    <span className="font-mono">{selectedUser.username}</span>
                                </div>
                            </div>

                            {!resetSuccess ? (
                                <>
                                    {/* Warning */}
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                                        <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
                                        <div className="text-sm text-amber-800">
                                            This will immediately change the user's password. Make sure to share the new password securely.
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div className="space-y-2">
                                        <Label>New Password</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                data-testid="reset-password-input"
                                                type="text"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="h-11 bg-white border-[#E5E5E5] font-mono"
                                            />
                                            <Button
                                                variant="outline"
                                                onClick={() => setNewPassword(generateRandomPassword())}
                                                className="h-11"
                                                title="Generate new password"
                                            >
                                                <RefreshCw size={16} />
                                            </Button>
                                        </div>
                                    </div>

                                    <Button
                                        data-testid="confirm-reset-btn"
                                        onClick={handleResetPassword}
                                        disabled={resettingPassword}
                                        className="w-full bg-[#E76F51] hover:bg-[#d45a3e] text-white rounded-full"
                                    >
                                        {resettingPassword ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                Resetting...
                                            </>
                                        ) : (
                                            'Reset Password'
                                        )}
                                    </Button>
                                </>
                            ) : (
                                /* Success State */
                                <div className="space-y-4">
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                        <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                                        <h3 className="font-medium text-green-900">Password Reset Successfully!</h3>
                                        <p className="text-sm text-green-700 mt-1">
                                            Share this password securely with the user
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <Label>New Password</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="text"
                                                value={generatedPassword}
                                                readOnly
                                                className="h-11 bg-[#F5F5F5] border-[#E5E5E5] font-mono text-lg"
                                            />
                                            <Button
                                                variant="outline"
                                                onClick={handleCopyPassword}
                                                className="h-11"
                                            >
                                                {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowResetDialog(false);
                                setResetSuccess(false);
                            }}
                            className="rounded-full"
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Users;
